/**
 * SRS (Spaced Repetition System) Utilities
 * Manages flashcard review scheduling based on difficulty ratings
 *
 * PHASE 2 TODO: Replace localStorage with backend API calls
 * PHASE 2 TODO: Implement SuperMemo2 or SM-2 algorithm for optimal spacing
 */

const SRS_STORAGE_KEY = 'chinese_flashcards_srs';

// Difficulty rating intervals (in minutes for now, will be days in production)
const INTERVALS = {
  again: 1,      // Review again in 1 minute
  hard: 10,      // Review in 10 minutes
  good: 1440,    // Review in 1 day (1440 minutes)
  easy: 4320,    // Review in 3 days (4320 minutes)
};

/**
 * Get SRS data from localStorage
 * @returns {Object} - SRS data with word reviews
 */
export function getSRSData() {
  const data = localStorage.getItem(SRS_STORAGE_KEY);
  return data ? JSON.parse(data) : {};
}

/**
 * Save SRS data to localStorage
 * @param {Object} data - SRS data to save
 */
function saveSRSData(data) {
  localStorage.setItem(SRS_STORAGE_KEY, JSON.stringify(data));
}

/**
 * Rate a word's difficulty and update review schedule
 *
 * @param {string} wordId - The ID of the word being rated
 * @param {string} rating - One of: 'again', 'hard', 'good', 'easy'
 * @returns {Object} - Updated word review data
 *
 * PHASE 2 TODO: Send this to backend API endpoint
 * Example: POST /api/srs/rate { wordId, rating, timestamp }
 */
export function rateDifficulty(wordId, rating) {
  if (!['again', 'hard', 'good', 'easy'].includes(rating)) {
    throw new Error(`Invalid rating: ${rating}`);
  }

  const srsData = getSRSData();
  const now = Date.now();
  const intervalMinutes = INTERVALS[rating];
  const nextReview = now + (intervalMinutes * 60 * 1000);

  // Update or create review record
  srsData[wordId] = {
    wordId,
    lastReviewed: now,
    nextReview,
    rating,
    reviewCount: (srsData[wordId]?.reviewCount || 0) + 1,
    history: [
      ...(srsData[wordId]?.history || []),
      { rating, timestamp: now }
    ]
  };

  saveSRSData(srsData);
  return srsData[wordId];
}

/**
 * Get the next queue of words to review based on SRS scheduling
 *
 * @param {Array} words - All available words
 * @param {Object} srsData - SRS review data (optional, will fetch if not provided)
 * @returns {Array} - Words that need review, sorted by priority
 *
 * Priority logic:
 * 1. New words (never reviewed)
 * 2. Words past their next review time (oldest first)
 * 3. Remaining words (for initial learning)
 *
 * PHASE 2 TODO: Fetch this from backend API
 * Example: GET /api/srs/queue?userId=xxx
 */
export function getNextWordQueue(words, srsData = null) {
  const data = srsData || getSRSData();
  const now = Date.now();

  const newWords = [];
  const dueWords = [];
  const futureWords = [];

  words.forEach(word => {
    const review = data[word.id];

    if (!review) {
      // Never reviewed - highest priority
      newWords.push({ ...word, priority: 0 });
    } else if (review.nextReview <= now) {
      // Due for review
      const overdue = now - review.nextReview;
      dueWords.push({ ...word, priority: overdue });
    } else {
      // Not due yet
      futureWords.push({ ...word, priority: review.nextReview });
    }
  });

  // Sort each category
  dueWords.sort((a, b) => b.priority - a.priority); // Most overdue first
  futureWords.sort((a, b) => a.priority - b.priority); // Soonest review first

  return [...newWords, ...dueWords, ...futureWords];
}

/**
 * Get statistics for a study session
 *
 * @param {Array} reviewedWords - Words reviewed in this session
 * @returns {Object} - Session statistics
 */
export function getSessionStats(reviewedWords) {
  const stats = {
    total: reviewedWords.length,
    again: 0,
    hard: 0,
    good: 0,
    easy: 0,
  };

  reviewedWords.forEach(word => {
    if (word.rating && stats.hasOwnProperty(word.rating)) {
      stats[word.rating]++;
    }
  });

  stats.accuracy = stats.total > 0
    ? ((stats.good + stats.easy) / stats.total * 100).toFixed(1)
    : 0;

  return stats;
}

/**
 * Reset all SRS data (useful for testing or starting over)
 * CAUTION: This deletes all progress
 */
export function resetSRSData() {
  localStorage.removeItem(SRS_STORAGE_KEY);
}

/**
 * Get review history for a specific word
 *
 * @param {string} wordId - The word ID to get history for
 * @returns {Array} - Array of review records
 */
export function getWordHistory(wordId) {
  const srsData = getSRSData();
  return srsData[wordId]?.history || [];
}
